<template>
	<div>
		<h1 class="p-text-bold p-my-0">Step 2: Program Selection</h1>

		<div
			v-if="store.membershipPayload && store.membershipPayload.locations"
			class="p-d-flex p-flex-column p-jc-around p-ai-center"
		>
			<span class="p-text-bold p-mt-3">Pick a location:</span>

			<div class="p-d-flex p-flex-column p-flex-sm-row p-ai-center p-mt-2">
				<div
					v-for="location in store.membershipPayload.locations"
					:key="location"
					class="p-field-radiobutton"
				>
					<RadioButton
						:id="location"
						name="location"
						:value="location"
						v-model="selectedLocation"
					/>

					<label :for="location" class="p-mr-3 p-ml-1">{{ location }}</label>
				</div>
			</div>
		</div>

		<div v-if="selectedLocation" class="p-mt-1">
			<div class="p-d-flex p-flex-column p-ai-center p-text-bold">
				<span>Please enter the number of children:</span>

				<p class="p-grid p-col-6 p-md-3 p-mt-0 p-pb-3">
					<InputNumber v-model="numOfChildren" :min="1" :max="10" showButtons />
				</p>
			</div>
		</div>

		<div v-if="selectedLocation">
			<div
				v-for="childNumber in numOfChildren"
				:key="childNumber"
				class="p-mt-2"
			>
				<div>
					<ChildInfo
						:childNumber="childNumber"
						:validateForm="validateForm"
						:validateAllChildrenInfoForms="validateAllChildrenInfoForms"
						:location="selectedLocation"
						@childTotal="getChildTotal"
					/>
				</div>
			</div>
		</div>

		<small
			v-if="v$.selectedLocation.$invalid && submitted"
			class="p-error p-text-bold p-pt-2"
		>
			{{ v$.selectedLocation.required.$message.replace("Value", "Location") }}
		</small>

		<div v-if="totalOfAllChildren != 0" class="p-text-center p-text-md-right p-mt-2">
			<div v-if="store.membershipPayload.additionalFees.length > 0">
				<h4 class="p-my-1">
					+ {{ store.membershipPayload.additionalFees[0].label }}: ${{
						store.membershipPayload.additionalFees[0].price.toFixed(2)
					}}
				</h4>
			</div>

			<h3 class="p-my-0 p-primary-color">
				Total: ${{ totalOfAllChildren.toFixed(2) }} (CAD)
			</h3>
		</div>

		<div class="p-d-flex p-jc-between p-mt-3 p-no-gutters">
			<div class="p-mb-3 p-col-6 p-pl-0">
				<Button
					label="Back"
					icon="pi pi-angle-left"
					class="p-button-secondary"
					style="min-width: 120px; max-width: 120px"
					@click="prevStep"
				/>
			</div>

			<div class="p-mb-3 p-col-6 p-pr-0">
				<Button
					label="Next"
					icon="pi pi-angle-right"
					iconPos="right"
					style="min-width: 120px; max-width: 120px"
					@click="nextStep"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { useFormDataStore } from "@/store/formData.js";
import ChildInfo from "@/components/blocks/ChildInfo.vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
	name: "ProgramSelection",
	components: {
		ChildInfo,
	},
	data() {
		return {
			numOfChildren: 1,
			validateForm: 0,
			totalOfAllChildren: 0,
			selectedLocation:
				this.store.membershipPayload &&
				this.store.membershipPayload.locations.length === 1
					? this.store.membershipPayload.locations[0]
					: null,
			submitted: false,
			allChildrenFormStates: [{}],
			showLocationError: false,
			childTotalArray: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
		};
	},
	setup() {
		const store = useFormDataStore();

		return {
			store,
			v$: useVuelidate(),
		};
	},
	validations() {
		return {
			selectedLocation: {
				required,
			},
		};
	},
	watch: {
		"store.Membership"(membership, oldMemebership) {
			if (
				oldMemebership !== null &&
				JSON.stringify(membership) !== JSON.stringify(oldMemebership)
			) {
				this.selectedLocation = null;

				this.totalOfAllChildren = 0;
			}
		},
		"store.membershipPayload.locations"(locations) {
			if (locations.length === 1) {
				this.selectedLocation = locations[0];
			}
		},
	},
	methods: {
		async nextStep() {
			this.submitted = true;
			await this.v$.$validate();

			this.validateForm += 1;
			for (let i = 1; i <= this.numOfChildren; i++) {
				this.allChildrenFormStates[i] = false;
			}
		},
		prevStep() {
			this.$emit("prevStep");
		},
		validateAllChildrenInfoForms(validChildInfoForm, childNumber) {
			this.allChildrenFormStates[childNumber] = validChildInfoForm;
			let validStates = Object.values(this.allChildrenFormStates);
			let allFormsValid = true;

			for (let i = 0; i <= this.numOfChildren; i++) {
				if (validStates[i] === false) {
					allFormsValid = false;
				}
			}

			if (allFormsValid) {
				this.store.ProgramSelection = {
					totalOfAllChildren: this.totalOfAllChildren,
					location: this.selectedLocation,
					numOfChildren: this.numOfChildren,
				};

				this.$emit("nextStep");
			} else {
				setTimeout(() => {
					this.scrollToFirstError();
				}, 700);
			}
		},
		getChildTotal(childTotal, childNumber) {
			if (this.selectedLocation) {
				this.childTotalArray[childNumber] = childTotal;
				this.totalOfAllChildren = 0;
				this.childTotalArray.forEach((amount) => {
					this.totalOfAllChildren += amount;
				});
				this.store.membershipPayload.additionalFees.length > 0
					? (this.totalOfAllChildren +=
							this.store.membershipPayload.additionalFees[0].price)
					: null;
			}
		},
		scrollToFirstError() {
			this.$nextTick(() => {
				if (document.querySelector(".p-error")) {
					let domRect = document
						.querySelector(".p-error")
						.getBoundingClientRect();

					window.scrollTo({
						left: domRect.left + document.documentElement.scrollLeft,
						top: domRect.top + document.documentElement.scrollTop - 100,
						behavior: "smooth",
					});
				}
			});
		},
	},
};
</script>

<style lang="scss" scoped></style>
